import React, { useEffect, useState } from "react";
import { Segmented } from "../ui";
import { AdminPanel } from "../AdminPanel";
import { DataSources } from "../DataSources";
import { AccountIdentity } from "../AccountIdentity";
import { AccessLog } from "../AccessLog";
import { useUI } from "@/lib/theme";
import { api } from "@/api";
import pkg from "../../../package.json";

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-sm font-medium text-fg">{label}</span>
      {children}
    </div>
  );
}

export function SettingsDrawer({
  open,
  onClose,
  onLogout,
  onReload,
  isAdmin,
  devImpersonating,
  devDemoAthletes,
}: {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
  onReload: () => void;
  isAdmin: boolean;
  devImpersonating: boolean;
  devDemoAthletes: string[];
}) {
  const { lang, setLang, theme, setTheme, t } = useUI();
  const [adminOpen, setAdminOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [accessLogOpen, setAccessLogOpen] = useState(false);
  const [devBusy, setDevBusy] = useState(false);
  const es = lang === "es";
  const localDev =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "::1", "[::1]"].includes(window.location.hostname);

  const handleSeedAthlete = async () => {
    if (devBusy) return;
    setDevBusy(true);
    try {
      await api.devSeedAthlete();
      onClose();
      onReload();
    } finally {
      setDevBusy(false);
    }
  };

  const handleImpersonate = async (userId: string) => {
    if (devBusy) return;
    setDevBusy(true);
    try {
      await api.devImpersonate(userId);
      onClose();
      location.reload();
    } finally {
      setDevBusy(false);
    }
  };

  const handleStopImpersonate = async () => {
    if (devBusy) return;
    setDevBusy(true);
    try {
      await api.devStopImpersonate();
      onClose();
      location.reload();
    } finally {
      setDevBusy(false);
    }
  };

  const handleResetDemo = async () => {
    if (devBusy) return;
    setDevBusy(true);
    try {
      await api.devResetDemo();
      onClose();
      onReload();
    } finally {
      setDevBusy(false);
    }
  };

  const handleDeleteAthlete = async (userId: string) => {
    if (devBusy) return;
    const msg = es
      ? `¿Borrar el atleta demo ${userId.slice(0, 13)}? Esta acción no se puede deshacer.`
      : `Delete demo athlete ${userId.slice(0, 13)}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDevBusy(true);
    try {
      await api.devDeleteAthlete(userId);
      onClose();
      onReload();
    } finally {
      setDevBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
        {/* Cuando el Admin está abierto encima, él pinta su propio backdrop:
            ocultamos el de Ajustes para no apilar dos capas de black/40 (~64%). */}
        {!adminOpen && <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />}
        <aside className="relative z-10 flex h-full w-full max-w-xs flex-col border-l border-line bg-panel shadow-2xl">
          <header className="flex items-center justify-between border-b border-line px-5 py-4">
            <div className="flex flex-col gap-0.5">
              <h2 className="text-base font-bold">{es ? "Ajustes" : "Settings"}</h2>
              <span className="text-[10px] font-semibold text-muted">Beta(v{pkg.version})</span>
            </div>
            <button
              onClick={onClose}
              aria-label={es ? "Cerrar" : "Close"}
              className="rounded-lg p-1 text-muted transition hover:bg-panel2 hover:text-fg"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
            <SettingRow label={es ? "Tema" : "Theme"}>
              <Segmented
                ariaLabel="theme"
                value={theme}
                onChange={setTheme}
                options={[
                  { value: "light", label: "☀", title: es ? "Claro" : "Light" },
                  { value: "dark", label: "🌙", title: es ? "Oscuro" : "Dark" },
                ]}
              />
            </SettingRow>
            <SettingRow label={es ? "Idioma" : "Language"}>
              <Segmented
                ariaLabel="language"
                value={lang}
                onChange={setLang}
                options={[
                  { value: "es", label: "ES" },
                  { value: "en", label: "EN" },
                ]}
              />
            </SettingRow>
            <div className="border-t border-line pt-3">
              <button
                onClick={() => setSourcesOpen((v) => !v)}
                className="flex w-full items-center justify-between py-1 text-sm font-medium text-fg"
              >
                <span>{es ? "Fuentes de datos" : "Data sources"}</span>
                <svg viewBox="0 0 24 24" className={`h-4 w-4 text-muted transition-transform ${sourcesOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
              </button>
              {sourcesOpen && <div className="mt-3"><DataSources onReload={onReload} /></div>}
            </div>
            <div className="border-t border-line pt-3">
              <button
                onClick={() => setIdentityOpen((v) => !v)}
                className="flex w-full items-center justify-between py-1 text-sm font-medium text-fg"
              >
                <span>{t("identity_section")}</span>
                <svg viewBox="0 0 24 24" className={`h-4 w-4 text-muted transition-transform ${identityOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
              </button>
              {identityOpen && <div className="mt-3"><AccountIdentity /></div>}
            </div>
            <div className="border-t border-line pt-3">
              <button
                onClick={() => setAccessLogOpen((v) => !v)}
                className="flex w-full items-center justify-between py-1 text-sm font-medium text-fg"
              >
                <span>{t("audit_title")}</span>
                <svg viewBox="0 0 24 24" className={`h-4 w-4 text-muted transition-transform ${accessLogOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
              </button>
              {accessLogOpen && <div className="mt-3"><AccessLog /></div>}
            </div>
          </div>

          <footer className="space-y-2 border-t border-line px-5 py-4">
            {isAdmin && (
              <button
                onClick={() => setAdminOpen(true)}
                className="w-full rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-sm font-medium text-violet-600 transition hover:bg-violet-500/20 dark:text-violet-300"
              >
                {es ? "Admin" : "Admin"}
              </button>
            )}
            {localDev && (
              <button
                onClick={async () => {
                  await api.seedHealth();
                  onClose();
                  onReload?.();
                }}
                className="w-full rounded-lg border border-teal-500/40 bg-teal-500/10 px-3 py-2 text-sm font-medium text-teal-600 transition hover:bg-teal-500/20 dark:text-teal-300"
              >
                {es ? "Cargar salud demo" : "Load demo health"}
              </button>
            )}
            {localDev && (
              <div className="border-t border-line pt-2 mt-1 space-y-1.5">
                <p className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted">
                  {es ? "Demo entrenador" : "Coach demo"}
                </p>
                {devImpersonating ? (
                  <button
                    onClick={handleStopImpersonate}
                    disabled={devBusy}
                    className="w-full rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-3 py-2 text-sm font-medium text-indigo-600 transition hover:bg-indigo-500/20 disabled:opacity-60 dark:text-indigo-300"
                  >
                    {es ? "← Volver a mi cuenta" : "← Back to my account"}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSeedAthlete}
                      disabled={devBusy}
                      className="w-full rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-sm font-medium text-violet-600 transition hover:bg-violet-500/20 disabled:opacity-60 dark:text-violet-300"
                    >
                      {devBusy
                        ? es ? "Creando…" : "Creating…"
                        : es ? "Crear atleta demo" : "Create demo athlete"}
                    </button>
                    {devDemoAthletes.map((id) => (
                      <div key={id} className="flex gap-1.5">
                        <button
                          onClick={() => handleImpersonate(id)}
                          disabled={devBusy}
                          className="flex-1 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-sm font-medium text-sky-600 transition hover:bg-sky-500/20 disabled:opacity-60 dark:text-sky-300"
                        >
                          {es ? `Ver como ${id.slice(0, 13)}` : `View as ${id.slice(0, 13)}`}
                        </button>
                        <button
                          onClick={() => handleDeleteAthlete(id)}
                          disabled={devBusy}
                          title={es ? "Borrar este atleta demo" : "Delete this demo athlete"}
                          className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2.5 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-500/20 disabled:opacity-60 dark:text-rose-300"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {devDemoAthletes.length > 0 && (
                      <button
                        onClick={handleResetDemo}
                        disabled={devBusy}
                        className="w-full rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-500/20 disabled:opacity-60 dark:text-rose-300"
                      >
                        {es ? "Resetear demo" : "Reset demo"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            <button
              onClick={onLogout}
              className="w-full rounded-lg border border-line bg-panel2/60 px-3 py-2 text-sm font-medium text-muted transition hover:text-fg"
            >
              {es ? "Salir" : "Log out"}
            </button>
          </footer>
        </aside>
      </div>

      {isAdmin && <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />}
    </>
  );
}
